/**
 * Activity Type service, backed by Firestore (`activityTypes` collection,
 * seeded by scripts/seed-firestore.js). Grouping/sorting mirrors the old
 * GET /api/activity-types response.
 */

import { collection, getDocs } from 'firebase/firestore';
import { db } from '../../../../shared/firebase';

/**
 * Get all activity types grouped by content bucket
 * @returns {Promise<Array>} Array of objects with contentBucket and activities array
 */
export const getActivityTypes = async () => {
  try {
    const snap = await getDocs(collection(db, 'activityTypes'));

    const grouped = {};
    snap.forEach((d) => {
      const activityType = d.data();
      const bucket = activityType.contentBucket;
      if (!grouped[bucket]) {
        grouped[bucket] = [];
      }
      grouped[bucket].push({
        activityTypeId: activityType.activityTypeId,
        activityTitle: activityType.activityTitle,
        displayOrder: activityType.displayOrder,
      });
    });

    const result = Object.keys(grouped).map((contentBucket) => ({
      contentBucket,
      activities: grouped[contentBucket].sort((a, b) => a.displayOrder - b.displayOrder),
    }));

    // Explicit ordering: Problem solving -> Strategy & planning ->
    // (others alphabetically) -> Product management & innovation last.
    const getSortOrder = (bucket) => {
      if (bucket === 'Problem solving') return 0;
      if (bucket === 'Strategy & planning') return 1;
      if (bucket === 'Product management & innovation') return 4;
      return 2;
    };

    result.sort((a, b) => {
      const orderA = getSortOrder(a.contentBucket);
      const orderB = getSortOrder(b.contentBucket);
      if (orderA !== orderB) {
        return orderA - orderB;
      }
      return a.contentBucket.localeCompare(b.contentBucket);
    });

    return result;
  } catch (error) {
    console.error('Error fetching activity types:', error);
    throw error;
  }
};
